import { useMemo, useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Helmet } from 'react-helmet-async'

const PAGE_URL = 'https://www.kronos.se/klockmarken/seiko'
const URBREV_SEIKO_URL = 'https://www.urbrev.se/varumarken/seiko.html'
// TODO: byt till en bild du har rättigheter till
const HERO_IMAGE =
  'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSG8LlzRoMppUaVWN2cpxKDCdu_mEcYNaCZOhH9pOK2kA&s=10'

const navigation = [
  { name: 'Kronos köpskydd', href: '/kronos-secure' },
  { name: 'Kronos Certified', href: '/kronos-certified' },
  { name: 'Kontrollera certifiering', href: '/kronos-certified-control' },
  { name: 'Klockvärdering', href: '/klockvardering' },
  { name: 'Företagsannonsering', href: '/foretagsannonsering' },
  { name: 'Marknadsföring', href: '/marknadsforing' },
  { name: 'Blogg', href: '/blogg' },
  { name: 'Integritetspolicy', href: '/integritetspolicy' },
  { name: 'Användarvillkor', href: '/anvandarvillkor' },
]

const quickLinks = [
  { name: 'Köpa Seiko', href: '#kopa' },
  { name: 'Sälja Seiko', href: '#salja' },
  { name: 'Seiko service', href: '#service' },
]

const models = [
  {
    name: 'Seiko 5 Sports',
    since: '1963',
    category: 'Vardag och fält',
    description:
      'Seikos mest kända vardagsklocka med automatiskt urverk, datum och veckodag. Tålig, prisvärd och finns i många färger och utföranden.',
    suits: 'dig som vill ha en robust och prisvärd mekanisk klocka',
  },
  {
    name: 'Prospex Turtle',
    since: '1976',
    category: 'Sport och dyk',
    description:
      'Dykarklockan med kuddformad boett som fått sitt smeknamn efter formen. Älskad av samlare och dykare och finns i flera moderna tolkningar.',
    suits: 'dig som vill ha en karaktärsfull dykarklocka',
  },
  {
    name: 'Prospex 1965 Diver’s',
    since: '1965',
    category: 'Sport och dyk',
    description:
      'Moderna tolkningar av Seikos första dykarklocka. Retroinspirerad design med modern konstruktion.',
    suits: 'dig som gillar vintagedesign men vill ha modern tillförlitlighet',
  },
  {
    name: 'Alpinist',
    since: '1961',
    category: 'Vardag och fält',
    description:
      'Fältklocka byggd för bergsklättring och friluftsliv, med tydlig urtavla och en inre kompassbezel i vissa modeller.',
    suits: 'dig som vill ha en lättläst och mångsidig klocka för vardag och friluftsliv',
  },
  {
    name: 'Presage',
    since: '2016',
    category: 'Klassisk',
    description:
      'Seikos kollektion med mekaniska dressklockor och ofta mycket vackra urtavlor, bland annat med emalj- eller lackinspiration.',
    suits: 'dig som vill ha en elegant mekanisk klocka med hantverkskänsla',
  },
  {
    name: 'King Seiko',
    since: '1961',
    category: 'Klassisk',
    description:
      'Historisk linje med skarpa, geometriska former och polerade ytor. Retroinspirerad design som tilltalar många samlare.',
    suits: 'dig som gillar vintageinspirerad design med tydlig japansk karaktär',
  },
  {
    name: 'Astron',
    since: '1969',
    category: 'Teknik och kronograf',
    description:
      'Namnet på världens första kvartsarmbandsur. Dagens Astron-modeller bygger vidare med solcell och GPS-teknik.',
    suits: 'dig som vill ha en klocka med avancerad teknik som sällan behöver skötas',
  },
  {
    name: 'Speedtimer',
    since: '1969',
    category: 'Teknik och kronograf',
    description:
      'Kronograf med rötter i Seikos första automatiska kronografer. Moderna versioner har tydlig, sportig design.',
    suits: 'dig som gillar kronografer med tydlig historia',
  },
]

const categories = [
  'Alla modeller',
  'Sport och dyk',
  'Vardag och fält',
  'Klassisk',
  'Teknik och kronograf',
]

const buyingSteps = [
  {
    title: 'Kontrollera referens- och serienummer',
    text: 'Numren graveras på klockan och ska stämma med papper och säljarens uppgifter. De kan också ge en fingervisning om tillverkningsperiod och exakt modell.',
  },
  {
    title: 'Fråga efter låda och papper',
    text: 'En klocka med originallåda, garantikort och övriga handlingar kallas ofta "full set" och är i regel mer attraktiv vid en framtida försäljning.',
  },
  {
    title: 'Fråga om modifieringar',
    text: 'Seiko-klockor modifieras ofta med ny bezel, glas, urtavla eller visare. Fråga om delarna är original och hur det påverkar värdet.',
  },
  {
    title: 'Be om servicehistorik',
    text: 'Fakturor från tidigare service visar hur klockan har skötts. Vid kvartsmodeller är det även bra att veta när batteriet senast byttes.',
  },
  {
    title: 'Handla tryggt',
    text: 'Välj en säljare du kan lita på och använd skydd som Kronos köpskydd, eller välj en klocka som är certifierad via Kronos Certified, för att minska risken i affären.',
  },
]

const sellingSteps = [
  {
    title: 'Ta reda på värdet',
    text: 'Priset påverkas av modell, referens, årsmodell, skick och om du har låda och papper. Börja med en klockvärdering så att du vet vad som är ett rimligt pris.',
    href: '/klockvardering',
    linkText: 'Värdera din Seiko',
  },
  {
    title: 'Samla papper och tillbehör',
    text: 'Originallåda, garantikort, kvitton och servicefakturor gör klockan mer tillförlitlig i köparens ögon och kan hjälpa dig att få ett bättre pris.',
  },
  {
    title: 'Ta bra bilder',
    text: 'Fotografera urtavla, boett, kanter, länkar, lås och baksida i dagsljus. Visa också eventuella repor, så undviker du frågor och förtroendeproblem.',
  },
  {
    title: 'Beskriv klockan ärligt',
    text: 'Ange referensnummer, årsmodell, skick, tidigare service och eventuella utbytta eller modifierade delar. En tydlig annons ger fler seriösa köpare.',
  },
  {
    title: 'Sälj på en plats där köparna är trygga',
    text: 'Annonsera på Kronos, där köpare letar efter klockor från både privata säljare och professionella handlare.',
    href: '/',
    linkText: 'Annonsera på Kronos',
  },
]

const serviceIncludes = [
  'Genomgång och rengöring av urverket',
  'Ny smörjning av rörliga delar',
  'Byte av slitna delar och packningar',
  'Test av vattentäthet',
  'Justering av gångnoggrannhet',
  'Vid behov varsam ytbehandling av boett och länk',
]

const trustCards = [
  {
    title: 'Kronos köpskydd',
    text: 'Handla med extra trygghet när du köper klockor av andra via Kronos.',
    href: '/kronos-secure',
  },
  {
    title: 'Kronos Certified',
    text: 'Verifierade och säkra affärer. Tjänsten utförs av vår officiella certifieringspartner UrBrev.se.',
    href: '/kronos-certified',
  },
  {
    title: 'Klockvärdering',
    text: 'Ta reda på vad din Seiko är värd innan du säljer eller köper nytt.',
    href: '/klockvardering',
  },
]

const faqs = [
  {
    question: 'Är det säkert att köpa en begagnad Seiko på Kronos?',
    answer:
      'Kronos erbjuder köpskydd och tjänsten Kronos Certified, som utförs av vår officiella certifieringspartner UrBrev.se, för dig som vill handla med extra trygghet. Läs mer om hur de fungerar på respektive sida, och kontrollera alltid klockans uppgifter, papper och säljare innan du köper.',
  },
  {
    question: 'Hur vet jag om en Seiko är äkta?',
    answer:
      'Kontrollera att referens och serienummer stämmer med handlingarna och granska klockans detaljer noggrant. För extra trygghet erbjuder vi Kronos Certified certifiering i samarbete med vår officiella certifieringspartner UrBrev.se, som kan kontrollera klockans äkthet, skick och funktion. Läs mer om Kronos Certified.',
  },
  {
    question: 'Vad betyder "full set"?',
    answer:
      'Det betyder att klockan säljs tillsammans med originallåda, garantikort och övriga handlingar som följde med vid köpet. Det är ofta eftertraktat och kan påverka värdet.',
  },
  {
    question: 'Vad betyder "5" i Seiko 5?',
    answer:
      'Namnet kommer från fem egenskaper som Seiko lyfte fram när serien lanserades 1963: automatiskt urverk, dag och datum, vattentäthet, infälld krona och en tålig konstruktion.',
  },
  {
    question: 'Vad är en modifierad Seiko?',
    answer:
      'Det är en klocka där ägaren eller en tredje part har bytt delar, till exempel bezel, glas, urtavla, visare eller band. Det är vanligt på Seiko och kan vara helt okej, men bör anges tydligt i annonsen och påverkar ofta värdet.',
  },
  {
    question: 'Påverkar polering värdet på en Seiko?',
    answer:
      'Ja, det kan det göra. Överdriven polering kan runda av kanter och förändra boettens form, och många samlare föredrar klockor som är opolerade eller varsamt behandlade.',
  },
  {
    question: 'Hur säljer jag min Seiko?',
    answer:
      'Börja med en klockvärdering så att du vet vad klockan kan vara värd, samla låda och papper, ta tydliga bilder och beskriv klockan ärligt. Sedan kan du annonsera på Kronos, där både privatpersoner och professionella handlare säljer klockor. Är du företag finns även företagsannonsering.',
  },
  {
    question: 'Vad ingår i en Seiko service?',
    answer:
      'En service innebär normalt att urverket plockas isär, rengörs och smörjs på nytt, att slitna delar och packningar byts, att vattentätheten testas och att gångnoggrannheten justeras. Vid kvartsklockor ingår ofta även batteribyte. Exakt innehåll varierar mellan verkstäder och modeller, så be alltid om ett kostnadsförslag.',
  },
  {
    question: 'Hur ofta behöver en Seiko service?',
    answer:
      'Intervallet varierar mellan modeller och kalibrar, och det beror också på hur ofta klockan bärs och hur den har skötts. Mekaniska klockor behöver normalt service med några års mellanrum, medan kvartsklockor främst behöver batteribyte och kontroll av vattentätheten. Tecken som ojämn gång, imma under glaset eller en krona som känns trög kan visa att klockan behöver tittas på tidigare. Vill du veta vad just din Seiko behöver kan du kontakta vår certifieringspartner UrBrev.se, som kan bedöma klockans skick och ge ett kostnadsförslag.',
  },
  {
    question: 'Var kan jag få min Seiko servad?',
    answer:
      'Vår officiella certifieringspartner UrBrev.se är en oberoende verkstad som utför service av Seiko-klockor. Du kan läsa mer och begära kostnadsförslag hos UrBrev.',
  },
  {
    question: 'Vem utför Kronos Certified?',
    answer: 'Kronos Certified utförs av vår officiella certifieringspartner UrBrev.se.',
  },
]

const facts = [
  { value: '1881', label: 'Grundades' },
  { value: '1963', label: 'Seiko 5' },
  { value: '1969', label: 'Astron' },
]

function SiteHeader({ siteLogo }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <header className="fixed inset-x-0 top-0 z-50 bg-white shadow">
      <nav className="flex items-center justify-between p-6 lg:px-8">
        <a href="/" className="-m-1.5 flex items-center p-1.5">
          <span className="sr-only">Kronos</span>
          <img alt="Kronos Logo" src={siteLogo} className="h-6 w-auto rounded-md" />
        </a>

        <div className="flex lg:hidden">
          <button
            type="button"
            className="rounded-md p-2.5 text-gray-700"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Öppna meny</span>
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
              />
            </svg>
          </button>
        </div>

        <div className="hidden lg:flex lg:gap-x-8">
          {navigation.map((item) => (
            <a
              key={item.name}
              href={item.href}
              className="text-sm font-semibold text-gray-900 hover:underline"
            >
              {item.name}
            </a>
          ))}
        </div>
      </nav>

      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-50" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full max-w-sm overflow-y-auto bg-white px-6 py-6 shadow-lg">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">Stäng meny</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 space-y-2">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
              >
                {item.name}
              </a>
            ))}
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  )
}

function StepList({ steps }) {
  return (
    <ol className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {steps.map((step, index) => (
        <li key={step.title} className="flex flex-col rounded-[28px] bg-[#f4f3f0] p-7">
          <span className="mb-5 flex h-10 w-10 items-center justify-center rounded-full bg-black text-sm font-bold text-white">
            {index + 1}
          </span>
          <h3 className="mb-3 text-lg font-bold tracking-tight">{step.title}</h3>
          <p className="text-sm leading-7 text-gray-600">{step.text}</p>
          {step.href && (
            <a href={step.href} className="mt-5 text-sm font-semibold underline">
              {step.linkText} <span aria-hidden="true">→</span>
            </a>
          )}
        </li>
      ))}
    </ol>
  )
}

export default function SeikoBrand({ siteLogo }) {
  const [activeCategory, setActiveCategory] = useState('Alla modeller')

  const filteredModels = useMemo(
    () =>
      activeCategory === 'Alla modeller'
        ? models
        : models.filter((model) => model.category === activeCategory),
    [activeCategory],
  )

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Kronos', item: 'https://www.kronos.se/' },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Klockmärken',
            item: 'https://www.kronos.se/klockmarken',
          },
          { '@type': 'ListItem', position: 3, name: 'Seiko', item: PAGE_URL },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    ],
  }

  return (
    <>
      <Helmet>
        <title>Köp Seiko, sälj Seiko & Seiko service | Kronos</title>
        <meta
          name="description"
          content="Vill du köpa eller sälja en Seiko? Handla tryggt på Kronos, få din Seiko värderad och läs om Seiko service hos vår certifieringspartner UrBrev."
        />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:title" content="Köp Seiko, sälj Seiko & Seiko service | Kronos" />
        <meta
          property="og:description"
          content="Köp och sälj Seiko tryggt på Kronos, och läs om Seiko service hos vår certifieringspartner UrBrev."
        />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="website" />
      </Helmet>

      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>

      {/* -mt-24 tar bort layoutens mt-24 så att hero går upp under headern */}
      <div className="-mt-24 min-h-screen bg-[#f4f3f0] font-poppins text-black">
        <SiteHeader siteLogo={siteLogo} />

        {/* HERO */}
        <section className="relative isolate overflow-hidden bg-[#e6e5e1] text-black">
          {/* Klockbilden placeras till höger och tonas in mot texten */}
          <div className="pointer-events-none absolute inset-y-0 right-0 w-full md:w-[58%]">
            <img
              src={HERO_IMAGE}
              alt="Seiko Prospex dykarklocka med svart urtavla"
              className="h-full w-full object-cover object-center"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#e6e5e1] via-[#e6e5e1]/75 to-transparent md:via-[#e6e5e1]/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#e6e5e1]/40 via-transparent to-white/10" />
          </div>

          <div className="relative z-10 mx-auto flex min-h-[650px] max-w-7xl items-center px-5 pb-14 pt-32 sm:px-8 md:min-h-[700px] md:pb-16 lg:px-10">
            <div className="w-full max-w-3xl">
              <nav aria-label="Brödsmulor" className="mb-8 text-xs font-medium text-gray-600">
                <ol className="flex flex-wrap items-center gap-2">
                  <li>
                    <a href="/" className="transition hover:text-black hover:underline">
                      Kronos
                    </a>
                  </li>
                  <li aria-hidden="true">/</li>
                  <li>
                    <a href="/klockmarken" className="transition hover:text-black hover:underline">
                      Klockmärken
                    </a>
                  </li>
                  <li aria-hidden="true">/</li>
                  <li aria-current="page" className="font-semibold text-black">
                    Seiko
                  </li>
                </ol>
              </nav>

              <span className="mb-7 inline-flex rounded-full border border-white/70 bg-white/70 px-5 py-2 text-xs font-extrabold tracking-[0.22em] backdrop-blur">
                KRONOS · SEIKO
              </span>

              <h1 className="mb-6 max-w-2xl text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
                Köp, sälj och serva Seiko tryggt.
              </h1>

              <p className="max-w-xl text-base leading-relaxed text-gray-700 sm:text-lg">
                Ska du köpa Seiko, sälja Seiko eller behöver din klocka Seiko service? Här hittar du
                populära modeller, tydliga guider för köp och försäljning och information om service
                hos vår certifieringspartner UrBrev.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="/"
                  className="inline-flex items-center gap-4 rounded-full bg-black px-7 py-4 text-sm font-semibold text-white transition hover:bg-gray-800"
                >
                  Hitta Seiko på Kronos <span aria-hidden="true">↗</span>
                </a>
                <a
                  href="/klockvardering"
                  className="inline-flex items-center gap-4 rounded-full border border-black/15 bg-white/75 px-7 py-4 text-sm font-semibold text-black transition hover:border-black/40 hover:bg-white"
                >
                  Värdera din Seiko <span aria-hidden="true">→</span>
                </a>
              </div>

              <ul className="mt-7 flex flex-wrap gap-2">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="inline-flex rounded-full border border-black/10 bg-white/70 px-4 py-2 text-xs font-semibold text-gray-700 transition hover:border-black/30 hover:text-black"
                    >
                      {link.name}{' '}
                      <span aria-hidden="true" className="ml-2">
                        ↓
                      </span>
                    </a>
                  </li>
                ))}
              </ul>

              <dl className="mt-10 grid max-w-2xl grid-cols-3 gap-3 border-t border-black/15 pt-5 sm:mt-12 sm:gap-5 sm:pt-6">
                {facts.map((fact) => (
                  <div key={fact.label}>
                    <dd className="text-2xl font-black tracking-tight sm:text-3xl md:text-4xl">
                      {fact.value}
                    </dd>
                    <dt className="mt-1 text-[10px] font-medium uppercase tracking-[0.12em] text-gray-600 sm:text-[11px] sm:tracking-[0.14em]">
                      {fact.label}
                    </dt>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        {/* ABOUT */}
        <section className="mx-auto max-w-4xl px-6 py-16 md:py-24">
          <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.25em] text-gray-500">
            Om märket
          </p>
          <h2 className="mb-7 text-4xl font-black tracking-tight md:text-5xl">
            Varför är Seiko så eftertraktat?
          </h2>
          <div className="space-y-5 text-base leading-8 text-gray-700">
            <p>
              Seiko grundades 1881 i Tokyo och har sedan dess blivit ett av världens mest kända
              urmärken. Märket introducerade Astron 1969, världens första kvartsarmbandsur, och har
              sedan dess drivit utvecklingen med både mekaniska och elektroniska klockor.
            </p>
            <p>
              Det som gör märket extra intressant på andrahandsmarknaden är bredden och
              prisnivåerna. Det finns allt från prisvärda vardagsklockor som Seiko 5 till
              samlarobjekt och avancerade modeller, och många modeller har en stark fan-gemenskap.
              Seiko tillverkar dessutom många av sina urverk själva.
            </p>
            <p>
              Samtidigt är Seiko ett märke där många klockor har modifierats med delar från andra
              tillverkare. Därför är det extra viktigt att veta vad du ska titta på och vem du köper
              av.
            </p>
          </div>
        </section>

        {/* MODELS */}
        <section id="modeller" className="mx-auto max-w-7xl px-5 pb-16 md:px-8 md:pb-24">
          <div className="mb-9">
            <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.25em] text-gray-500">
              Modeller
            </p>
            <h2 className="text-4xl font-black tracking-tight md:text-5xl">
              Populära Seiko-modeller
            </h2>
          </div>

          <div className="mb-10 flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                aria-pressed={activeCategory === category}
                className={`rounded-full border px-5 py-3 text-xs font-semibold transition ${
                  activeCategory === category
                    ? 'border-black bg-black text-white'
                    : 'border-black/10 bg-white/70 text-gray-600 hover:border-black/30 hover:text-black'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {filteredModels.map((model) => (
              <article
                key={model.name}
                className="flex min-w-0 flex-col rounded-[28px] bg-white p-7 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="mb-6 flex items-center justify-between gap-3">
                  <span className="rounded-full bg-[#f4f3f0] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-600">
                    {model.category}
                  </span>
                  <span className="text-xs font-semibold text-gray-400">Sedan {model.since}</span>
                </div>
                <h3 className="mb-3 text-2xl font-black tracking-tight">{model.name}</h3>
                <p className="mb-6 text-sm leading-7 text-gray-600">{model.description}</p>
                <p className="mt-auto border-t border-black/5 pt-5 text-xs leading-6 text-gray-500">
                  <span className="font-bold uppercase tracking-[0.15em] text-gray-400">
                    Passar
                  </span>{' '}
                  {model.suits}
                </p>
              </article>
            ))}
          </div>

          <p className="mt-8 text-sm text-gray-500">
            Vill du jämföra med andra märken? Se alla{' '}
            <a href="/klockmarken" className="font-semibold text-black underline">
              klockmärken på Kronos
            </a>
            .
          </p>
        </section>

        {/* BUY */}
        <section id="kopa" className="scroll-mt-28 bg-white px-6 py-16 md:py-24">
          <div className="mx-auto max-w-7xl">
            <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.25em] text-gray-500">
              Köpguide
            </p>
            <h2 className="mb-5 max-w-3xl text-4xl font-black tracking-tight md:text-5xl">
              Köpa Seiko: så gör du en trygg affär
            </h2>
            <p className="mb-12 max-w-3xl text-base leading-8 text-gray-600">
              En begagnad Seiko kan vara en bra affär, men det finns saker som är värda att
              kontrollera innan du bestämmer dig. Gå igenom de här stegen när du ska köpa en Seiko.
            </p>
            <StepList steps={buyingSteps} />
            <div className="mt-10">
              <a
                href="/"
                className="inline-flex items-center gap-4 rounded-full bg-black px-7 py-4 text-sm font-semibold text-white transition hover:bg-gray-800"
              >
                Se Seiko till salu på Kronos <span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
        </section>

        {/* SELL */}
        <section id="salja" className="mx-auto max-w-7xl scroll-mt-28 px-5 py-16 md:px-8 md:py-24">
          <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.25em] text-gray-500">
            Sälj din klocka
          </p>
          <h2 className="mb-5 max-w-3xl text-4xl font-black tracking-tight md:text-5xl">
            Sälja Seiko: så får du rätt pris
          </h2>
          <p className="mb-12 max-w-3xl text-base leading-8 text-gray-600">
            Vill du sälja din Seiko? Med rätt förberedelser blir både annonsen och affären enklare,
            och du når köpare som vet vad de letar efter.
          </p>
          <StepList steps={sellingSteps} />
          <p className="mt-8 text-sm text-gray-500">
            Säljer du som företag? Läs mer om{' '}
            <a href="/foretagsannonsering" className="font-semibold text-black underline">
              företagsannonsering på Kronos
            </a>
            .
          </p>
        </section>

        {/* SERVICE */}
        <section id="service" className="scroll-mt-28 bg-white px-6 py-16 md:py-24">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-2">
            <div>
              <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.25em] text-gray-500">
                Underhåll
              </p>
              <h2 className="mb-6 text-4xl font-black tracking-tight md:text-5xl">
                Seiko service: därför och när
              </h2>
              <div className="space-y-5 text-base leading-8 text-gray-600">
                <p>
                  Med tiden slits smörjmedel och mekaniska delar, även i en klocka som sällan
                  används. En regelbunden Seiko service ger jämnare gång, bättre tillförlitlighet
                  och en längre livslängd, och en dokumenterad servicehistorik gör klockan tryggare
                  att sälja.
                </p>
                <p>
                  Hur ofta det behövs beror på modell, kaliber och användning. Kvartsklockor behöver
                  främst batteribyte och kontroll av vattentätheten, medan äldre klockor och
                  samlarobjekt kräver särskilt varsamt arbete där originaldelar och boettens form
                  bör bevaras.
                </p>
                <p>
                  Vår officiella certifieringspartner{' '}
                  <a
                    href={URBREV_SEIKO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-black underline"
                  >
                    UrBrev
                  </a>{' '}
                  är en oberoende verkstad som utför service av Seiko-klockor. Hos dem kan du läsa
                  mer och begära kostnadsförslag.
                </p>
              </div>
              <a
                href={URBREV_SEIKO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-4 rounded-full bg-black px-7 py-4 text-sm font-semibold text-white transition hover:bg-gray-800"
              >
                Seiko service hos UrBrev <span aria-hidden="true">↗</span>
              </a>
            </div>

            <div className="rounded-[28px] bg-[#f4f3f0] p-8 md:p-10">
              <h3 className="mb-6 text-xl font-bold tracking-tight">
                Vad ingår normalt i en service?
              </h3>
              <ul className="space-y-4">
                {serviceIncludes.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-7 text-gray-700">
                    <span
                      aria-hidden="true"
                      className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-black text-[10px] text-white"
                    >
                      ✓
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-6 border-t border-black/10 pt-5 text-xs leading-6 text-gray-500">
                Innehållet varierar mellan verkstäder och modeller. Be alltid om ett kostnadsförslag
                innan arbetet påbörjas.
              </p>
            </div>
          </div>
        </section>

        {/* TRUST */}
        <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.25em] text-gray-500">
            Trygg handel
          </p>
          <h2 className="mb-5 max-w-3xl text-4xl font-black tracking-tight md:text-5xl">
            Handla med trygghet på Kronos
          </h2>
          <p className="mb-12 max-w-3xl text-base leading-8 text-gray-600">
            Kronos Certified utförs av vår officiella certifieringspartner UrBrev.se och ger
            verifierade och säkra affärer. Tillsammans med köpskydd och klockvärdering får du ett
            tydligare beslutsunderlag, oavsett om du köper eller säljer.
          </p>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {trustCards.map((card) => (
              <a
                key={card.title}
                href={card.href}
                className="group flex flex-col rounded-[28px] border border-black/5 bg-white p-7 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="mb-3 text-xl font-bold tracking-tight">{card.title}</h3>
                <p className="mb-8 text-sm leading-7 text-gray-600">{card.text}</p>
                <span className="mt-auto text-sm font-semibold">
                  Läs mer{' '}
                  <span
                    aria-hidden="true"
                    className="inline-block transition group-hover:translate-x-1"
                  >
                    →
                  </span>
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-4xl px-6 pb-16 md:pb-24">
          <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.25em] text-gray-500">
            Vanliga frågor
          </p>
          <h2 className="mb-10 text-4xl font-black tracking-tight md:text-5xl">
            Frågor om att köpa, sälja och serva Seiko
          </h2>

          <div className="divide-y divide-black/10 rounded-[28px] bg-white px-6 shadow-sm md:px-8">
            {faqs.map((faq) => (
              <details key={faq.question} className="group py-6">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-base font-bold md:text-lg [&::-webkit-details-marker]:hidden">
                  {faq.question}
                  <span
                    aria-hidden="true"
                    className="shrink-0 text-2xl font-light transition group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>
                <p className="mt-4 text-sm leading-7 text-gray-600 md:text-base">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-white px-6 py-20 text-center md:py-28">
          <p className="mb-5 text-xs font-extrabold uppercase tracking-[0.25em] text-gray-500">
            Din nästa Seiko väntar
          </p>
          <h2 className="mx-auto mb-7 max-w-4xl text-4xl font-black leading-tight tracking-tight md:text-6xl">
            Köp, sälj eller värdera.
            <br />
            Allt på Kronos.
          </h2>
          <p className="mx-auto mb-8 max-w-xl text-sm leading-7 text-gray-600 md:text-base">
            Utforska klockor från privata säljare och professionella handlare, eller ta reda på vad
            din egen Seiko är värd.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="/"
              className="inline-flex items-center gap-4 rounded-full bg-black px-7 py-4 text-sm font-semibold text-white transition hover:bg-gray-800"
            >
              Upptäck Kronos <span aria-hidden="true">↗</span>
            </a>
            <a
              href="/klockvardering"
              className="inline-flex items-center gap-4 rounded-full border border-black/15 px-7 py-4 text-sm font-semibold text-black transition hover:border-black/40"
            >
              Värdera din klocka <span aria-hidden="true">→</span>
            </a>
          </div>
        </section>

        <p className="mx-auto max-w-5xl px-6 py-7 text-center text-[11px] leading-6 text-gray-500">
          Seiko är ett registrerat varumärke som tillhör Seiko Group Corporation. Kronos och UrBrev
          är inte anslutna till Seiko. Märkes- och modellnamn används i informativt syfte, och
          modellurvalet är vägledande och inte en fullständig lista.
        </p>
      </div>
    </>
  )
}
